import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Slider,
  List,
  ListItem,
  Checkbox,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
  Paper,
  Box,
  FormControlLabel,
  CircularProgress,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { useStore } from "../StoreContext/StoreContext";
import ProductList from "./ProductsList";
import { ProductListAPI } from "../../Utility/UtilApi/UtilApi";
import "../../App.css";
import { MenuIcon } from "../../Data/image";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { fontFamilyall } from "../../UtilColorPalate/UtilColorPalate";

export default function ShopPage() {
  const { cart } = useStore();
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(["All"]);
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500]); // Changed from [0, 50]
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [isCategoriesOpen, setCategoriesOpen] = useState(true);
  const [products, setProducts] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [ageOptions, setAgeOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currencyCode, setCurrencyCode] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = {};
      const response = await ProductListAPI(data);
      console.log("Fetched products:", response?.data);
      const products = response?.data?.products || [];
      setProducts(products);
      setShopCategories(response?.data?.productcategories?.map((cat) => cat.lable) || []);
      setAgeOptions(response?.data?.age?.map((age) => age.lable) || []);
      setGenderOptions(response?.data?.gender?.map((gender) => gender.lable) || []);
      setCurrencyCode(response?.data?.currencycode);
      if (products.length > 0) {
        const prices = products.map((p) => p.unitpriceinclvat ?? 0);
        const minPrice = Math.floor(Math.min(...prices));
        const maxPrice = Math.ceil(Math.max(...prices));
        setPriceRange([minPrice, maxPrice]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) => {
      if (category === "All") {
        return prev.includes("All") ? [] : ["All"];
      } else {
        const newCategories = prev.includes(category)
          ? prev.filter((c) => c !== category && c !== "All")
          : [...prev.filter((c) => c !== "All"), category];
        return newCategories;
      }
    });
  };

  const handleAgeChange = (age) => {
    setSelectedAges((prev) =>
      prev.includes(age) ? prev.filter((a) => a !== age) : [...prev, age]
    );
  };

  const handleGenderChange = (gender) => {
    setSelectedGenders((prev) =>
      prev.includes(gender) ? prev.filter((g) => g !== gender) : [...prev, gender]
    );
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  const filteredAndSortedProducts = () => {
    let filtered = products?.filter((product) => {
      const productCategoryNames = (
        Array.isArray(product.productcategories)
          ? product.productcategories
          : []
      ).map((cat) => cat.name);
      const productPrice = product.unitpriceinclvat ?? 0;
      const productAge = product.age?.name;
      const productGender = product.gender?.name;

      const matchesPrice = productPrice >= priceRange[0] && productPrice <= priceRange[1];

      const hasSpecificFilters =
        (selectedCategories.length > 0 && !selectedCategories.includes("All")) ||
        selectedAges.length > 0 ||
        selectedGenders.length > 0;

      if (!hasSpecificFilters) {
        return matchesPrice;
      }

      const matchesCategory =
        selectedCategories.includes("All") ||
        selectedCategories.some((selectedCat) => productCategoryNames.includes(selectedCat));
      
      const matchesAge = selectedAges.includes(productAge);
      const matchesGender = selectedGenders.includes(productGender);

      return matchesPrice && (matchesCategory || matchesAge || matchesGender);
    });

    if (sortOrder === "lowToHigh") {
      filtered.sort((a, b) => (a.unitpriceinclvat ?? 0) - (b.unitpriceinclvat ?? 0));
    } else if (sortOrder === "highToLow") {
      filtered.sort((a, b) => (b.unitpriceinclvat ?? 0) - (a.unitpriceinclvat ?? 0));
    }

    return filtered;
  };

  const allCategories = ["All", ...shopCategories];

  const renderFilterSection = (title, items, selectedItems, handleChange) => (
    <>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#fff",
          fontWeight: "bold",
          fontFamily: fontFamilyall,
          bgcolor: "#8c7aa2",
          p: 1,
          borderRadius: "8px",
          mt: 2,
        }}
      >
        {title}
      </Typography>
      <List sx={{ py: 0 }}>
        {items.map((item, index) => (
          <ListItem
            key={index}
            sx={{
              py: 0,
              px: 0.5,
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": { bgcolor: "#e0d6f5", transform: "translateX(5px)" },
              fontFamily: fontFamilyall,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedItems.includes(item)}
                  onChange={() => handleChange(item)}
                  sx={{
                    color: "#8c7aa2",
                    "&.Mui-checked": { color: "#6b5b8e" },
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                    p: 0.5,
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: fontFamilyall,
                    fontSize: "0.9rem",
                    fontWeight: selectedItems.includes(item) ? "600" : "400",
                    color: selectedItems.includes(item) ? "#6b5b8e" : "#333",
                  }}
                >
                  {item}
                </Typography>
              }
              sx={{
                fontFamily: fontFamilyall,
                width: "100%",
                m: 0,
                p: 0.5,
                borderRadius: 2,
                bgcolor: selectedItems.includes(item) ? "rgba(140, 122, 162, 0.2)" : "transparent",
              }}
            />
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <Box
      sx={{
        p: 3,
        fontFamily: fontFamilyall,
        background: "#f9f9f9",
        paddingTop: isMobile ? "25%" : "9%",
      }}
    >
      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={2.5}>
          <Paper
            elevation={4}
            sx={{
              p: 2,
              mt: isMobile ? 5 : 0,
              borderRadius: 3,
              width: "90%",
              background: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {isMobile ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fontFamilyall,
                      fontSize: "1rem",
                      fontWeight: "500",
                      color: "#8c7aa2",
                      mr: 1,
                    }}
                  >
                    All Filters
                  </Typography>
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{ color: "#8c7aa2" }}
                  >
                    <WidgetsIcon />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  PaperProps={{ sx: { width: 250 } }}
                >
                  {/* Category Title */}
                  <MenuItem sx={{ px: 2, py: 1, pointerEvents: "none" }}>
                    <Typography
                      sx={{
                        fontFamily: fontFamilyall,
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#6b5b8e",
                      }}
                    >
                      Category
                    </Typography>
                  </MenuItem>
                  {allCategories.map((category, index) => (
                    <MenuItem key={index} sx={{ px: 2, py: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedCategories.includes(category)}
                            onChange={() => handleCategoryChange(category)}
                            sx={{
                              color: "#8c7aa2",
                              "&.Mui-checked": { color: "#6b5b8e" },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: fontFamilyall,
                              fontSize: "1rem",
                              fontWeight: selectedCategories.includes(category) ? "600" : "400",
                              color: selectedCategories.includes(category) ? "#6b5b8e" : "#333",
                            }}
                          >
                            {category}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </MenuItem>
                  ))}

                  {/* Age Groups Title */}
                  <MenuItem sx={{ px: 2, py: 1, pointerEvents: "none" }}>
                    <Typography
                      sx={{
                        fontFamily: fontFamilyall,
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#6b5b8e",
                      }}
                    >
                      Age Groups
                    </Typography>
                  </MenuItem>
                  {ageOptions.map((age, index) => (
                    <MenuItem key={index} sx={{ px: 2, py: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedAges.includes(age)}
                            onChange={() => handleAgeChange(age)}
                            sx={{
                              color: "#8c7aa2",
                              "&.Mui-checked": { color: "#6b5b8e" },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "1rem",
                              fontWeight: selectedAges.includes(age) ? "600" : "400",
                              color: selectedAges.includes(age) ? "#6b5b8e" : "#333",
                            }}
                          >
                            {age}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </MenuItem>
                  ))}

                  {/* Gender Title */}
                  <MenuItem sx={{ px: 2, py: 1, pointerEvents: "none" }}>
                    <Typography
                      sx={{
                        fontFamily: fontFamilyall,
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "#6b5b8e",
                      }}
                    >
                      Gender
                    </Typography>
                  </MenuItem>
                  {genderOptions.map((gender, index) => (
                    <MenuItem key={index} sx={{ px: 2, py: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedGenders.includes(gender)}
                            onChange={() => handleGenderChange(gender)}
                            sx={{
                              color: "#8c7aa2",
                              "&.Mui-checked": { color: "#6b5b8e" },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "1rem",
                              fontWeight: selectedGenders.includes(gender) ? "600" : "400",
                              color: selectedGenders.includes(gender) ? "#6b5b8e" : "#333",
                            }}
                          >
                            {gender}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <>
                {renderFilterSection("Categories", allCategories, selectedCategories, handleCategoryChange)}
                {renderFilterSection("Age Groups", ageOptions, selectedAges, handleAgeChange)}
                {renderFilterSection("Gender", genderOptions, selectedGenders, handleGenderChange)}
              </>
            )}
          </Paper>
        </Grid>

        {/* Product List */}
        <Grid item xs={12} md={9} ml={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              px: { xs: 1, sm: 0 },
            }}
          >
            <FormControl sx={{ minWidth: { xs: "100%", sm: 150 }, maxWidth: 200 }}>
              <InputLabel
                id="sort-label"
                sx={{
                  fontSize: "0.9rem",
                  color: "#6b5b8e",
                  "&.Mui-focused": { color: "#4a3b6d" },
                }}
              >
                Sort by Price
              </InputLabel>
              <Select
                labelId="sort-label"
                value={sortOrder}
                label="Sort by Price"
                onChange={handleSortChange}
                sx={{
                  fontFamily: fontFamilyall,
                  height: 40,
                  fontSize: "0.9rem",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#8c7aa2" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6b5b8e" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4a3b6d",
                    borderWidth: 2,
                  },
                  "& .MuiSelect-select": { display: "flex", alignItems: "center" },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <MenuItem value="lowToHigh" sx={{ fontSize: "0.9rem", color: "#333" }}>
                  Price Low to High
                </MenuItem>
                <MenuItem value="highToLow" sx={{ fontSize: "0.9rem", color: "#333" }}>
                  Price High to Low
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={60} thickness={5} sx={{ color: "#8c7aa2" }} />
              <Typography
                variant="h6"
                sx={{ fontFamily: fontFamilyall, color: "#6b5b8e", fontWeight: "bold" }}
              >
                Loading Products...
              </Typography>
            </Box>
          ) : filteredAndSortedProducts().length > 0 ? (
            <ProductList
              filteredProducts={filteredAndSortedProducts()}
              setHoveredProductId={setHoveredProductId}
              currencyCode={currencyCode}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: fontFamilyall,
                color: "#888",
                fontSize: "1.2rem",
                textAlign: "center",
                mt: 4,
              }}
            >
              No products found for selected filters.
            </Typography>
          )}
        </Grid>
      </Grid>

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Box>
  );
}